import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, TrendingUp, CreditCard, Percent } from 'lucide-react';
import { useVisibility } from '@/hooks/useVisibility';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface CustomerFinancialsProps {
  jobs: any[];
  totalLifetimeValue: number;
}

export function CustomerFinancials({ jobs, totalLifetimeValue }: CustomerFinancialsProps) {
  const { canSeeCompanyFinancials, canSeeProfit } = useVisibility();
  const { formatMoney, formatShortDate } = useCompanyLocale();
  const totalRevenue = jobs.reduce((sum, job) => sum + (job.invoice_total || 0), 0);
  const totalOutstanding = jobs.reduce((sum, job) => sum + (job.amount_outstanding || 0), 0);
  const averageJobValue = jobs.length > 0 ? totalRevenue / jobs.length : 0;
  const paidJobs = jobs.filter(job => job.payment_status === 'paid').length;
  const paymentCompletionRate = jobs.length > 0 ? (paidJobs / jobs.length) * 100 : 0;

  return (
    <div className="space-y-6">
      {canSeeCompanyFinancials && (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Lifetime Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatMoney(totalLifetimeValue)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatMoney(totalRevenue)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Outstanding</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatMoney(totalOutstanding)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Job Value</CardTitle>
            <Percent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatMoney(averageJobValue)}</div>
            <p className="text-xs text-muted-foreground">
              {paymentCompletionRate.toFixed(0)}% payment rate
            </p>
          </CardContent>
        </Card>
      </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Jobs Financial Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-2 text-left text-sm font-medium">Job Number</th>
                  <th className="p-2 text-left text-sm font-medium">Date</th>
                  <th className="p-2 text-right text-sm font-medium">Quoted</th>
                  <th className="p-2 text-right text-sm font-medium">Revenue</th>
                  <th className="p-2 text-right text-sm font-medium">Parts Cost</th>
                  {canSeeProfit && <th className="p-2 text-right text-sm font-medium">Profit</th>}
                  <th className="p-2 text-right text-sm font-medium">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id} className="border-b">
                    <td className="p-2 text-sm">{job.job_number}</td>
                    <td className="p-2 text-sm">
                      {job.scheduled_date ? formatShortDate(job.scheduled_date) : 'N/A'}
                    </td>
                    <td className="p-2 text-right text-sm">{formatMoney(job.quoted_price || 0)}</td>
                    <td className="p-2 text-right text-sm">{formatMoney(job.invoice_total || 0)}</td>
                    <td className="p-2 text-right text-sm">{formatMoney(job.total_parts_cost || 0)}</td>
                    {canSeeProfit && (
                      <td className="p-2 text-right text-sm">{formatMoney(job.company_profit || 0)}</td>
                    )}
                    <td className="p-2 text-right text-sm">{formatMoney(job.amount_outstanding || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
